import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Menu, MenuItem as NativeMenuItem } from "@mui/material";
import studentMasterApi from "./studentMasterApi";
import { UploadStudentsDialog, UploadPhotosDialog } from "./StudentImportDialogs";
import DownloadIcon from "@mui/icons-material/Download";
import ListItemIcon from "@mui/material/ListItemIcon";
const blue = "#1976d2";
const red = "#d32f2f";
const preventDialogBackdropClose = (event, reason) => {
  if (reason === "backdropClick" || reason === "escapeKeyDown") {
    return;
  }
};
const swalDialogOptions = {
  target: document.body,
  heightAuto: false,
  didOpen: () => {
    const container = Swal.getContainer();
    if (container) {
      container.style.zIndex = "20000";
    }
  },
};
const emptyStudent = {
  student_first_name: "",
  student_middle_name: "",
  student_sur_last_name: "",
  dob: "",
  gender: "O",
  aadhaar_no: "",
  mobile: "",
  email: "",
  admission_no: "",
  admission_date: "",
  admission_type: "REGULAR",
  seat_type: "C",
  section: "A",
  second_language: "TELUGU",
  student_rollno: "",
  father_name: "",
  mother_name: "",
  guardian_name: "",
  guardian_relation: "",
  religion: "",
  caste: "",
  subcaste: "",
  blood_group: "",
  nationality: "INDIAN",
  address_line1: "",
  address_line2: "",
  village_town: "",
  mandal: "",
  district: "",
  state: "ANDHRA PRADESH",
  pincode: "",
  extra_attributes: {},
};

const readList = (data) => {
  if (Array.isArray(data)) return data;
  return data?.data || data?.rows || data?.items || [];
};

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  "Something went wrong.";

function StudentFormDialog({ open, onClose, selection, student, lookups, onSaved }) {
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(emptyStudent);
  const [saving, setSaving] = useState(false);
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");

  useEffect(() => {
    if (!open) return;
    setTab(0);
    setForm({
      ...emptyStudent,
      ...(student || {}),
      admittedbatch_id: selection?.batchId,
      program_id: selection?.programId,
      branch_id: selection?.branchId,
      dob: student?.dob ? String(student.dob).slice(0, 10) : "",
      admission_date: student?.admission_date ? String(student.admission_date).slice(0, 10) : "",
      aadhaar_no: student?.aadhaar_no,
      email: student?.email,
      mobile: student?.mobile,
      religion: student?.religion,
      caste: student?.caste,
      subcaste: student?.subcaste,
      second_language: student?.second_language,
    });
  }, [open, student, selection]);

  const setField = (name, value) => setForm((old) => ({ ...old, [name]: value }));

  const submit = async () => {
    if (!String(form.student_first_name || "").trim()) {
      Swal.fire({
        title: "Required",
        text: "Enter the student's first name.",
        icon: "warning",
        didOpen: () => {
          const container = Swal.getContainer();
          if (container) container.style.zIndex = "20000";
        },
      });
      setTab(0);
      return;
    }
    if (!String(form.student_sur_last_name || "").trim()) {
      Swal.fire({
        title: "Required",
        text: "Enter the student's surname.",
        icon: "warning",
        didOpen: () => {
          const container = Swal.getContainer();
          if (container) container.style.zIndex = "20000";
        },
      });
      setTab(0);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        admittedbatch_id: selection.batchId,
        program_id: selection.programId,
        branch_id: selection.branchId,
        guardian_relation: form.guardian_relation || null,
        religion: form.religion || null,
        caste: form.caste || null,
        subcaste: form.subcaste || null,
        blood_group: form.blood_group || null,
        second_language: form.second_language || null,
        userEmail: user.user_email,
        userId: user.user_id,
      };
      await studentMasterApi.saveStudent(student?.student_id, payload);
      await onSaved();
      onClose();
    } catch (error) {
      Swal.fire({
        title: "Unable to save student",
        text: getErrorMessage(error),
        icon: "error",
        didOpen: () => {
          const container = Swal.getContainer();
          if (container) {
            container.style.zIndex = "20000";
          }
        },
      });
    } finally {
      setSaving(false);
    }
  };

  const nativeSelect = (name, label, options, valueKey, labelKey) => (
    <TextField
      select
      fullWidth
      size="medium"
      label={label}
      value={form[name] ?? ""}
      onChange={(e) => setField(name, e.target.value)}
      InputLabelProps={{ shrink: true }}
      SelectProps={{ native: true }}
    >
      <option value=""></option>
      {(options || []).map((item) => (
        <option key={item[valueKey]} value={item[valueKey]}>
          {item[labelKey]}
        </option>
      ))}
    </TextField>
  );

  const text = (name, label, props = {}) => (
    <TextField
      fullWidth
      size="medium"
      label={label}
      value={form[name] ?? ""}
      onChange={(e) => setField(name, e.target.value)}
      InputLabelProps={{ shrink: true }}
      {...props}
    />
  );

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { height: "90vh" } }}
    >
      <DialogTitle
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          pr: 7,
        }}
      >
        <Typography fontWeight={700}>{student ? "Edit Student" : "Add Student"}</Typography>
        <Typography variant="body2" color="text.secondary">
          {selection?.batchName} · {selection?.programName} · {selection?.branchName}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 12, color: red }}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}
      >
        {["Personal", "Admission", "Family", "Address", "Education", "Certificates", "Other"].map(
          (label) => (
            <Tab key={label} label={label} />
          )
        )}
      </Tabs>
      <DialogContent dividers sx={{ pt: 2 }}>
        {tab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              {text("student_rollno", "Student Roll No. (assigned on admission)", {
                disabled: true,
              })}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("aadhaar_no", "Aadhaar Number")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("mobile", "Mobile Number")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("student_first_name", "First Name", { required: true })}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("student_middle_name", "Middle Name")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("student_sur_last_name", "Surname", { required: true })}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("dob", "Date of Birth", { type: "date" })}
            </Grid>
            <Grid item xs={12} sm={4}>
              {nativeSelect(
                "gender",
                "Gender",
                [
                  { value: "M", label: "Male" },
                  { value: "F", label: "Female" },
                  { value: "O", label: "Other" },
                ],
                "value",
                "label"
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("aadhaar_no", "Aadhaar Number")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("mobile_no", "Mobile Number")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("email", "Email", { type: "email" })}
            </Grid>

            <Grid item xs={12} sm={4}>
              {text("nationality", "Nationality")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {nativeSelect(
                "religion",
                "Religion",
                lookups?.religions,
                "religion_code",
                "religion_name"
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {nativeSelect(
                "blood_group",
                "Blood Group",
                lookups?.bloodGroups,
                "blood_group_code",
                "blood_group_name"
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {nativeSelect("caste", "Caste", lookups?.castes, "caste_code", "caste_name")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {nativeSelect(
                "subcaste",
                "Subcaste",
                (lookups?.subcastes || []).filter(
                  (x) => !form.caste || String(x.caste_code) === String(form.caste)
                ),
                "subcaste_code",
                "subcaste_name"
              )}
            </Grid>
          </Grid>
        )}
        {tab === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              {text("admission_no", "Admission Number")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("admission_date", "Admission Date", { type: "date" })}
            </Grid>
            <Grid item xs={12} sm={4}>
              {nativeSelect(
                "admission_type",
                "Admission Type",
                [
                  { value: "REGULAR", label: "Regular" },
                  { value: "LATERAL", label: "Lateral Entry" },
                  { value: "TRANSFER", label: "Transfer" },
                ],
                "value",
                "label"
              )}
            </Grid>

            <Grid item xs={12} sm={4}>
              {nativeSelect(
                "seat_type",
                "Seat Type",
                [
                  { value: "C", label: "Convener" },
                  { value: "M", label: "Management" },
                  { value: "S", label: "Spot" },
                ],
                "value",
                "label"
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("section", "Section")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {nativeSelect(
                "second_language",
                "Second Language",
                ["TELUGU", "HINDI", "SANSKRIT", "URDU", "FRENCH"].map((value) => ({
                  value,
                  label: value,
                })),
                "value",
                "label"
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("scholar_app_no", "Scholar Application Number")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("aapara_id", "Aapara Number")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("oamdc_no", " OAMDC Number")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("enroll_status", "Enrollment Status", { disabled: true })}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("workflow_status", "Workflow Status", { disabled: true })}
            </Grid>
          </Grid>
        )}
        {tab === 2 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              {text("father_name", "Father Name")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("mother_name", "Mother Name")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("guardian_name", "Guardian Name")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {nativeSelect(
                "guardian_relation",
                "Guardian Relation",
                lookups?.familyRelations,
                "relation_code",
                "relation_name"
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("father_mobile", "Father Mobile")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("mother_mobile", "Mother Mobile")}
            </Grid>
          </Grid>
        )}
        {tab === 3 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {text("door_no", "DoorNo/PlotNo")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {text("street", "Location/Street")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("village", "Village / Town")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("mandal", "Mandal")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("district", "District")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("state", "State")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("pincode", "PIN Code")}
            </Grid>
          </Grid>
        )}
        {tab === 4 && (
          <Grid container spacing={2}>
            {[
              ["ssc_school_name", "SSC School"],
              ["ssc_year", "SSC Year"],
              ["ssc_cgpa", "SSC CGPA"],
              ["inter_college_name", "Intermediate College"],
              ["inter_year", "Intermediate Year"],
              ["inter_cgpa", "Intermediate Marks"],
              ["diploma_college_name", "Diploma College"],
              ["diploma_year", "Diploma Year"],
              ["diploma_cgpa", "Diploma Marks"],
              ["degree_college_name", "Degree College"],
              ["degree_year", "Degree Year"],
              ["degree_cgpa", "Degree Marks"],
              ["pg_college_name", "PG College"],
              ["pg_year", "PG Year"],
              ["pg_cgpa", "PG Marks"],
            ].map(([name, label]) => (
              <Grid item xs={12} sm={4} key={name}>
                {text(name, label)}
              </Grid>
            ))}
          </Grid>
        )}
        {tab === 5 && (
          <Grid container spacing={2}>
            {[
              ["caste_certificate_no", "Caste Certificate Number"],
              ["income_certificate_no", "Income Certificate Number"],
              ["ews_certificate_no", "EWS Certificate Number"],
              ["ration_card_no", "Ration Card Number"],
              ["ssc_tc_no", "SSC TC No"],
              ["ssc_tc_date", "SSC TC Date"],
              ["cet_hallticket_no", "CET Hall Ticket Number"],
              ["cet_rank", "CET Rank"],
              ["phc_status", "PHC Status"],
            ].map(([name, label]) => (
              <Grid item xs={12} sm={4} key={name}>
                {text(name, label)}
              </Grid>
            ))}
            <Grid item xs={12} sm={4}>
              {nativeSelect(
                "certificate_status",
                "Certificate Status",
                ["YES", "NO"].map((value) => ({
                  value,
                  label: value,
                })),
                "value",
                "label"
              )}
            </Grid>
          </Grid>
        )}
        {tab === 6 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              {text("mole1", "Identification Mole 1")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("mole2", "Identification Mole 2")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("previous_college", "Previous College")}
            </Grid>
            <Grid item xs={12} sm={4}>
              {text("transfer_reason", "Transfer / Rejoin Details")}
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ position: "sticky", bottom: 0, bgcolor: "background.paper", p: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: "#ebedef",
            backgroundColor: "#eb3123",
            textTransform: "none",
            "&:hover": { backgroundColor: "#e83b14", borderColor: "#1976d2", color: "#ebedef" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={submit}
          variant="contained"
          disabled={saving}
          sx={{
            color: "#ebedef",
            backgroundColor: "#234eeb",
            textTransform: "none",
            "&:hover": { backgroundColor: "#1454e8", borderColor: "#1976d2", color: "#ebedef" },
          }}
        >
          {saving ? (
            <CircularProgress size={20} color="inherit" />
          ) : student ? (
            "Update Student"
          ) : (
            "Save Student"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function StudentActions({ row, onEdit, onDelete, onChangeProgram, onChangeBranch }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <IconButton size="medium" onClick={(e) => setAnchor(e.currentTarget)}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <NativeMenuItem
          onClick={() => {
            setAnchor(null);
            onEdit(row);
          }}
        >
          Edit Student
        </NativeMenuItem>
        <NativeMenuItem
          onClick={() => {
            setAnchor(null);
            onChangeProgram(row);
          }}
        >
          Change Program
        </NativeMenuItem>
        <NativeMenuItem
          onClick={() => {
            setAnchor(null);
            onChangeBranch(row);
          }}
        >
          Change Branch
        </NativeMenuItem>
        <NativeMenuItem
          sx={{ color: red }}
          onClick={() => {
            setAnchor(null);
            onDelete(row);
          }}
        >
          Deactivate Student
        </NativeMenuItem>
      </Menu>
    </>
  );
}

function StudentsDialog({ open, onClose, selection, lookups, onRefresh }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async () => {
    if (!open || !selection) return;
    setLoading(true);
    try {
      const result = await studentMasterApi.getStudents(selection, {
        page: page + 1,
        pageSize,
        search,
      });
      setRows(readList(result));
      setTotal(Number(result?.total ?? result?.totalCount ?? readList(result).length));
    } catch (e) {
      Swal.fire({
        title: "Unable to load students",
        text: getErrorMessage(e),
        icon: "error",
        didOpen: () => {
          const container = Swal.getContainer();
          if (container) container.style.zIndex = "20000";
        },
      });
    } finally {
      setLoading(false);
    }
  }, [open, selection, page, pageSize, search]);

  useEffect(() => {
    load();
  }, [load]);

  const deleteStudent = async (row) => {
    const result = await Swal.fire({
      ...swalDialogOptions,
      title: "Deactivate this student?",
      text: "Historical fee, attendance and examination records should be retained.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Deactivate",
      confirmButtonColor: red,
    });
    if (!result.isConfirmed) return;
    try {
      await studentMasterApi.deactivateStudent(row.student_id);
      await load();
      await onRefresh();
      Swal.fire({
        title: "Updated",
        text: "Student was deactivated.",
        icon: "success",
        didOpen: () => {
          const container = Swal.getContainer();
          if (container) container.style.zIndex = "20000";
        },
      });
    } catch (e) {
      Swal.fire({
        title: "Unable to deactivate",
        text: getErrorMessage(e),
        icon: "error",
        didOpen: () => {
          const container = Swal.getContainer();
          if (container) container.style.zIndex = "20000";
        },
      });
    }
  };

  const changeAssignment = async (row, kind) => {
    const options = kind === "program" ? lookups?.programs || [] : lookups?.branches || [];

    const { value } = await Swal.fire({
      ...swalDialogOptions,
      title: kind === "program" ? "Change Program" : "Change Branch",
      input: "select",
      inputOptions: Object.fromEntries(
        options.map((o) => [o.program_id ?? o.branch_id, o.program_name ?? o.branch_name])
      ),
      inputPlaceholder: `Select ${kind}`,
      showCancelButton: true,
      confirmButtonColor: blue,
    });
    if (!value) return;
    try {
      if (kind === "program") await studentMasterApi.changeProgram(row.student_id, Number(value));
      else await studentMasterApi.changeBranch(row.student_id, Number(value));
      await load();
      await onRefresh();
    } catch (e) {
      Swal.fire({
        title: `Unable to change ${kind}`,
        text: getErrorMessage(e),
        icon: "error",
        didOpen: () => {
          const container = Swal.getContainer();
          if (container) container.style.zIndex = "20000";
        },
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "serial",
        headerName: "S.No.",
        width: 75,
        sortable: false,
        valueGetter: (p) => page * pageSize + p.row.__index + 1,
      },
      { field: "student_rollno", headerName: "Roll No.", minWidth: 160, flex: 1 },
      {
        field: "student_name",
        headerName: "Student Name",
        minWidth: 200,
        flex: 1,
        valueGetter: (p) =>
          p.row.student_name ||
          [p.row.student_first_name, p.row.student_middle_name, p.row.student_sur_last_name]
            .filter(Boolean)
            .join(" "),
      },
      { field: "seat_type", headerName: "Seat Type", width: 110 },
      {
        field: "dob",
        headerName: "DOB",
        width: 120,
        valueGetter: (p) => (p.row.dob ? String(p.row.dob).slice(0, 10) : ""),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <StudentActions
            row={p.row}
            onEdit={(r) => {
              setEditing(r);
              setFormOpen(true);
            }}
            onDelete={deleteStudent}
            onChangeProgram={(r) => changeAssignment(r, "program")}
            onChangeBranch={(r) => changeAssignment(r, "branch")}
          />
        ),
      },
    ],
    [page, pageSize, lookups]
  );

  return (
    <>
      <Dialog open={open} onClose={preventDialogBackdropClose} fullWidth maxWidth="xl">
        <DialogTitle sx={{ pr: 7 }}>
          <Typography fontWeight={700}>Students — {selection?.branchName}</Typography>
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", right: 12, top: 10, color: red }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mb={2}>
            <TextField
              size="medium"
              label="Search roll number / name"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
            />
            <Button
              variant="outlined"
              onClick={load}
              sx={{
                color: "#ebedef",
                backgroundColor: "#3423eb",
                textTransform: "none",
                "&:hover": { backgroundColor: "#141be8", borderColor: "#1976d2", color: "#ebedef" },
              }}
            >
              Search
            </Button>
            <Box flex={1} />
            <Typography variant="body2" alignSelf="center">
              Total students: {total}
            </Typography>
          </Stack>
          <Box sx={{ height: 460, width: "100%" }}>
            <DataGrid
              rows={rows.map((r, i) => ({ ...r, id: r.student_id, __index: i }))}
              columns={columns}
              loading={loading}
              paginationMode="server"
              rowCount={total}
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            sx={{
              color: "#ebedef",
              backgroundColor: "#eb3123",
              textTransform: "none",
              "&:hover": { backgroundColor: "#e83b14", borderColor: "#1976d2", color: "#ebedef" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <StudentFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        selection={selection}
        student={editing}
        lookups={lookups}
        onSaved={async () => {
          await load();
          await onRefresh();
        }}
      />
    </>
  );
}

function AdmitStudentsDialog({ open, onClose, selection, onAdmitted }) {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [seatTypes, setSeatTypes] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newRollNo, setNewRollNo] = useState("");
  const [newRollNos, setNewRollNos] = useState({});
  const load = useCallback(async () => {
    if (!open || !selection) return;
    setLoading(true);
    try {
      const result = await studentMasterApi.getEligibleAdmissions(selection, {
        page: page + 1,
        pageSize,
        search,
      });

      const list = readList(result);
      setRows(list);
      setTotal(Number(result?.total ?? result?.totalCount ?? list.length));
    } catch (e) {
      Swal.fire({
        title: "Unable to load eligible students",
        text: getErrorMessage(e),
        icon: "error",
        didOpen: () => {
          const container = Swal.getContainer();
          if (container) container.style.zIndex = "20000";
        },
      });
    } finally {
      setLoading(false);
    }
  }, [open, selection, page, pageSize, search]);

  useEffect(() => {
    load();
  }, [load]);

  const admit = async (ids) => {
    if (!ids.length) {
      Swal.fire({
        title: "Select students",
        text: "Select at least one eligible student.",
        icon: "info",
        didOpen: () => {
          const container = Swal.getContainer();
          if (container) container.style.zIndex = "20000";
        },
      });
      return;
    }
    setSubmitting(true);
    try {
      const seatMap = {};
      ids.forEach((id) => {
        seatMap[id] = seatTypes[id] || "C";
      });
      const newRollNosMap = {};
      ids.forEach((id) => {
        newRollNosMap[id] = newRollNos[id] || "";
      });

      const result = await studentMasterApi.admitStudents(selection, ids, seatMap, newRollNosMap);
      await load();
      setSelected([]);
      await onAdmitted();
      Swal.fire({
        title: "Admission completed",
        text: result?.message || `${ids.length} student(s) admitted.`,
        icon: "success",
        didOpen: () => {
          const container = Swal.getContainer();
          if (container) container.style.zIndex = "20000";
        },
      });
    } catch (e) {
      Swal.fire({
        title: "Admission failed",
        text: getErrorMessage(e),
        icon: "error",
        didOpen: () => {
          const container = Swal.getContainer();
          if (container) container.style.zIndex = "20000";
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      field: "serial",
      headerName: "S.No.",
      width: 75,
      sortable: false,
      valueGetter: (p) => page * pageSize + p.row.__index + 1,
    },
    {
      field: "select",
      headerName: "Select",
      width: 90,
      sortable: false,
      renderCell: (p) => (
        <input
          type="checkbox"
          checked={selected.includes(p.row.id)}
          onChange={(e) =>
            setSelected((old) =>
              e.target.checked
                ? [...new Set([...old, p.row.id])]
                : old.filter((id) => id !== p.row.id)
            )
          }
        />
      ),
    },
    {
      field: "student_name",
      headerName: "Student Name",
      minWidth: 200,
      flex: 1,
      valueGetter: (p) =>
        p.row.student_name ||
        [p.row.student_first_name, p.row.student_middle_name, p.row.student_sur_last_name]
          .filter(Boolean)
          .join(" "),
    },
    {
      field: "seat_type",
      headerName: "Seat Type",
      width: 150,
      sortable: false,
      renderCell: (p) => (
        <select
          value={seatTypes[p.row.id] || p.row.seat_type || "C"}
          onChange={(e) => setSeatTypes((old) => ({ ...old, [p.row.id]: e.target.value }))}
        >
          <option value="C">Convener</option>
          <option value="M">Management</option>
          <option value="S">Spot</option>
        </select>
      ),
    },
    { field: "student_rollno", headerName: "Old RollNo.", minWidth: 140, flex: 1 },

    {
      field: "new_student_rollno",
      headerName: "New Roll No",
      width: 180,

      renderCell: (p) => (
        <TextField
          size="medium"
          fullWidth
          value={newRollNos[p.row.id] || ""}
          onChange={(e) => setNewRollNos((old) => ({ ...old, [p.row.id]: e.target.value }))}
          InputLabelProps={{
            shrink: true,
          }}
        />
      ),
    },

    {
      field: "admit_action",
      headerName: "Action",
      width: 150,
      sortable: false,
      renderCell: (p) => (
        <Button
          size="medium"
          variant="contained"
          disabled={submitting}
          onClick={() => admit([p.row.id])}
          sx={{
            color: "#ebedef",
            backgroundColor: "#1976d2",
            textTransform: "none",
            "&:hover": { backgroundColor: "#1490e8", borderColor: "#1976d2", color: "#ebedef" },
          }}
        >
          Admit Student
        </Button>
      ),
    },
  ];

  return (
    <Dialog open={open} onClose={preventDialogBackdropClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ pr: 7 }}>
        <Typography fontWeight={700}>
          Admit Students — {selection?.batchName} {selection?.programName} {selection?.branchName}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 12, top: 10, color: red }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Roll numbers are generated by the server only when admission is confirmed. Individual and
          bulk admissions must use the same transaction-safe sequence.
        </Alert>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mb={2}>
          <TextField
            size="medium"
            label="Search students"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
          />
          <Button
            variant="outlined"
            onClick={load}
            size="medium"
            sx={{
              color: "#ebedef",
              backgroundColor: "#192cd2",
              textTransform: "none",
              "&:hover": { backgroundColor: "#1544bd", borderColor: "#1976d2", color: "#ebedef" },
            }}
          >
            Search Student
          </Button>
          <Box flex={1} />
          <Button
            variant="contained"
            disabled={submitting || selected.length === 0}
            onClick={() => admit(selected)}
            startIcon={<HowToRegIcon />}
            sx={{
              color: "#ebedef",
              backgroundColor: "#1976d2",
              textTransform: "none",
              "&:hover": { backgroundColor: "#1490e8", borderColor: "#1976d2", color: "#ebedef" },
            }}
          >
            Admit Selected ({selected.length})
          </Button>
        </Stack>
        <Box sx={{ height: 460 }}>
          <DataGrid
            rows={rows.map((r, i) => ({ ...r, id: r.student_id ?? r.id, __index: i }))}
            columns={columns}
            loading={loading || submitting}
            paginationMode="server"
            rowCount={total}
            page={page}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            disableSelectionOnClick
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          sx={{
            color: "#ebedef",
            backgroundColor: "#eb3123",
            textTransform: "none",
            "&:hover": { backgroundColor: "#e83b14", borderColor: "#1976d2", color: "#ebedef" },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function StudentMaster() {
  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [branchRows, setBranchRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [lookups, setLookups] = useState({});
  const [notice, setNotice] = useState("");
  const [selected, setSelected] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [studentsOpen, setStudentsOpen] = useState(false);
  const [admitOpen, setAdmitOpen] = useState(false);
  const [uploadStudentsOpen, setUploadStudentsOpen] = useState(false);
  const [uploadPhotosOpen, setUploadPhotosOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [downloadingStudents, setDownloadingStudents] = useState(false);
  const [batchRows, setBatchRows] = useState([]);
  const loadBatches_old = useCallback(async () => {
    try {
      const list = readList(await studentMasterApi.getActiveBatches());
      const sorted = [...list].sort((a, b) =>
        String(b.admittedbatch || "").localeCompare(String(a.admittedbatch || ""), undefined, {
          numeric: true,
        })
      );
      setBatches(sorted);
      if (!batchId && sorted.length) setBatchId(String(sorted[0].admittedbatch_id));
    } catch (e) {
      setNotice(getErrorMessage(e));
    }
  }, [batchId]);
  const loadBatches = useCallback(async () => {
    try {
      const list = readList(await studentMasterApi.getActiveBatches());

      const sorted = [...list].sort((a, b) =>
        String(b.admittedbatch || "").localeCompare(String(a.admittedbatch || ""), undefined, {
          numeric: true,
        })
      );

      setBatches(sorted);
      setBatchRows(
        sorted.map((row, index) => ({
          ...row,
          id: row.admittedbatch_id,
          __index: index,
        }))
      );

      // Do not automatically select a batch.
      // First display the admitted batch summary.
    } catch (e) {
      setNotice(getErrorMessage(e));
    }
  }, []);
  const batchColumns = useMemo(
    () => [
      {
        field: "serial",
        headerName: "S.No.",
        width: 75,
        sortable: false,
        valueGetter: (p) => p.row.__index + 1,
      },
      {
        field: "admittedbatch",
        headerName: "Admitted Batch",
        minWidth: 180,
        flex: 1,
      },
      {
        field: "intake",
        headerName: "Intake",
        width: 110,
        renderCell: (p) => (
          <Chip
            label={p.value ?? 0}
            color="success"
            size="medium"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              bgcolor: "rgba(223, 80, 168, 0.1)",
            }}
          />
        ),
      },
      {
        field: "strength",
        headerName: "Strength",
        width: 110,
        renderCell: (p) => (
          <Chip
            label={p.value ?? 0}
            color="primary"
            size="medium"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              bgcolor: "rgba(223, 80, 168, 0.1)",
            }}
          />
        ),
      },
      {
        field: "enrolled",
        headerName: "Enrolled",
        width: 120,
        renderCell: (p) => (
          <Chip
            label={p.value ?? 0}
            color="success"
            size="medium"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              bgcolor: "rgba(223, 80, 168, 0.1)",
            }}
          />
        ),
      },
      {
        field: "admitted",
        headerName: "Admitted",
        width: 120,
        renderCell: (p) => (
          <Chip
            label={p.value ?? 0}
            color="info"
            size="medium"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              bgcolor: "rgba(223, 80, 168, 0.1)",
            }}
          />
        ),
      },
      {
        field: "drop",
        headerName: "Drop",
        width: 110,
        renderCell: (p) => (
          <Chip
            label={p.value ?? 0}
            color="error"
            size="medium"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              bgcolor: "rgba(223, 80, 168, 0.1)",
            }}
          />
        ),
      },
      {
        field: "show",
        headerName: "Action",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Button
            size="medium"
            variant="contained"
            onClick={() => {
              setBatchId(String(p.row.admittedbatch_id));
              setPage(0);
            }}
            sx={{
              textTransform: "none",
              backgroundColor: "#1976d2",
              color: "#ebedef",
              "&:hover": { backgroundColor: "#1490e8", color: "#ebedef" },
            }}
          >
            Show Branches
          </Button>
        ),
      },
    ],
    []
  );
  const loadBranches = useCallback(async () => {
    if (!batchId) {
      setBranchRows([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    try {
      const result = await studentMasterApi.getBatchBranches(batchId, { page: page + 1, pageSize });
      const list = readList(result);
      setBranchRows(
        list.map((r, i) => ({
          ...r,
          id: r.admittedbatch_branch_id ?? `${r.program_id}-${r.branch_id}`,
          __index: i,
        }))
      );
      setTotal(Number(result?.total ?? result?.totalCount ?? list.length));
    } catch (e) {
      setNotice(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [batchId, page, pageSize]);

  useEffect(() => {
    loadBatches();
  }, []); // initial load
  useEffect(() => {
    loadBranches();
  }, [loadBranches]);
  useEffect(() => {
    studentMasterApi
      .getLookups()
      .then((data) => setLookups(data?.data || data || {}))
      .catch(() => {});
  }, []);

  const openAction = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setMenuRow(row);
  };
  const notify = (message, severity = "success") => {
    setNotice(message);
  };
  const buildSelection = (row) => ({
    batchId: Number(batchId),
    batchName:
      batches.find((b) => String(b.admittedbatch_id) === String(batchId))?.admittedbatch || "",
    programId: Number(row.program_id),
    programName: row.program_name || row.program?.program_name || "",
    branchId: Number(row.branch_id),
    branchName: row.branch_name || row.branch?.branch_name || "",
  });
  const handleDownloadStudents = async (row) => {
    const selection = buildSelection(row);

    if (!selection.batchId || !selection.programId || !selection.branchId) {
      notify("Select a valid batch, program and branch.", "error");
      return;
    }

    try {
      setDownloadingStudents(true);
      await studentMasterApi.downloadStudents(selection);
      notify("Student CSV downloaded successfully.", "success");
    } catch (error) {
      notify(error?.response?.data?.message || "Unable to download student CSV.", "error");
    } finally {
      setDownloadingStudents(false);
      setMenuAnchor(null);
    }
  };
  const columns = useMemo(
    () => [
      {
        field: "serial",
        headerName: "S.No.",
        width: 75,
        valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
      },
      {
        field: "program_name",
        headerName: "Program",
        minWidth: 180,
        flex: 1,
        valueGetter: (p) => p.row.program_name || p.row.program?.program_name || "",
      },
      {
        field: "branch_name",
        headerName: "Branch",
        minWidth: 180,
        flex: 1,
        valueGetter: (p) => p.row.branch_name || p.row.branch?.branch_name || "",
      },
      {
        field: "intake",
        headerName: "Intake",
        width: 110,
        renderCell: (p) => (
          <Chip
            label={p.value ?? 0}
            color="success"
            size="medium"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              bgcolor: "rgba(223, 80, 168, 0.1)",
            }}
          />
        ),
      },
      {
        field: "strength",
        headerName: "Strength",
        width: 110,
        renderCell: (p) => (
          <Chip
            label={p.value ?? 0}
            color="success"
            size="medium"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              bgcolor: "rgba(223, 80, 168, 0.1)",
            }}
          />
        ),
      },
      {
        field: "enrolled",
        headerName: "Enrolled",
        width: 110,
        renderCell: (p) => (
          <Chip
            label={p.value ?? 0}
            color="success"
            size="medium"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              bgcolor: "rgba(223, 80, 168, 0.1)",
            }}
          />
        ),
      },
      {
        field: "admitted",
        headerName: "Admitted",
        width: 110,
        renderCell: (p) => (
          <Chip
            label={p.value ?? 0}
            color="error"
            size="medium"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              bgcolor: "rgba(223, 80, 168, 0.1)",
            }}
          />
        ),
      },
      {
        field: "active",
        headerName: "Active",
        width: 110,
        renderCell: (p) => (
          <Chip
            label={p.value ?? 0}
            color="info"
            size="medium"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              bgcolor: "rgba(223, 80, 168, 0.1)",
            }}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Tooltip title="Actions">
            <IconButton onClick={(e) => openAction(e, p.row)}>
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [page, pageSize]
  );

  const selection = menuRow ? buildSelection(menuRow) : selected;

  return (
    <Box p={{ xs: 1, md: 2 }}>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Student Master
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Admitted batch-wise student summary and branch management.
            </Typography>
          </Box>

          {batchId && (
            <Button
              variant="outlined"
              onClick={() => {
                setBatchId("");
                setPage(0);
              }}
              sx={{
                textTransform: "none",
                backgroundColor: "#1976d2",
                borderColor: "#1976d2",
                color: "#ebedef",
                "&:hover": {
                  backgroundColor: "#1976d2",
                  borderColor: "#1976d2",
                  color: "#ebedef",
                },
              }}
            >
              Back to Batches
            </Button>
          )}
        </Stack>
      </Paper>

      {/* ADMITTED BATCH SUMMARY */}
      {!batchId && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" fontWeight={700} mb={2}>
            Admitted Batch Summary
          </Typography>

          <Box sx={{ height: 460, width: "100%" }}>
            <DataGrid
              rows={batchRows}
              columns={batchColumns}
              loading={loading}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
            />
          </Box>
        </Paper>
      )}

      {/* SELECTED BATCH PROGRAM / BRANCH DETAILS */}
      {Boolean(batchId) && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={2}
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Program / Branch Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Admitted Batch:{" "}
                  {
                    batches.find((b) => String(b.admittedbatch_id) === String(batchId))
                      ?.admittedbatch
                  }
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Paper sx={{ p: 1 }}>
            <Box sx={{ height: 560, width: "100%" }}>
              <DataGrid
                rows={branchRows}
                columns={columns}
                loading={loading}
                paginationMode="server"
                rowCount={total}
                page={page}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                disableSelectionOnClick
              />
            </Box>
          </Paper>
        </>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
          setMenuRow(null);
        }}
      >
        <NativeMenuItem
          onClick={() => {
            setSelected(buildSelection(menuRow));
            setFormOpen(true);
            setMenuAnchor(null);
            setMenuRow(null);
          }}
        >
          <AddIcon fontSize="small" sx={{ mr: 1 }} />
          Add Student
        </NativeMenuItem>
        <MenuItem onClick={() => handleDownloadStudents(menuRow)} disabled={downloadingStudents}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
          Download Students
        </MenuItem>
        <NativeMenuItem
          onClick={() => {
            setSelected(buildSelection(menuRow));
            setUploadStudentsOpen(true);
            setMenuAnchor(null);
            setMenuRow(null);
          }}
        >
          <UploadFileIcon fontSize="small" sx={{ mr: 1 }} />
          Upload Students
        </NativeMenuItem>
        <NativeMenuItem
          onClick={() => {
            setSelected(buildSelection(menuRow));
            setUploadPhotosOpen(true);
            setMenuAnchor(null);
            setMenuRow(null);
          }}
        >
          <UploadFileIcon fontSize="small" sx={{ mr: 1 }} />
          Upload Photos
        </NativeMenuItem>
        <NativeMenuItem
          onClick={() => {
            setSelected(buildSelection(menuRow));
            setStudentsOpen(true);
            setMenuAnchor(null);
            setMenuRow(null);
          }}
        >
          <PersonSearchIcon fontSize="small" sx={{ mr: 1 }} />
          Show Students
        </NativeMenuItem>
        <NativeMenuItem
          onClick={() => {
            setSelected(buildSelection(menuRow));
            setAdmitOpen(true);
            setMenuAnchor(null);
            setMenuRow(null);
          }}
        >
          <HowToRegIcon fontSize="small" sx={{ mr: 1 }} />
          Admit Students
        </NativeMenuItem>
      </Menu>

      <StudentFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        selection={selected}
        student={null}
        lookups={lookups}
        onSaved={async () => {
          await loadBranches();
          setNotice("Student is  saved.");
        }}
      />
      <StudentsDialog
        open={studentsOpen}
        onClose={() => setStudentsOpen(false)}
        selection={selected}
        lookups={lookups}
        onRefresh={loadBranches}
      />
      <AdmitStudentsDialog
        open={admitOpen}
        onClose={() => setAdmitOpen(false)}
        selection={selected}
        onAdmitted={async () => {
          await loadBranches();
          setNotice("Admission completed.");
        }}
      />
      <UploadStudentsDialog
        open={uploadStudentsOpen}
        onClose={() => setUploadStudentsOpen(false)}
        selection={selected}
        onUploaded={(result) => {
          loadBranches();

          notify(
            result?.message ||
              `${result?.inserted || 0} inserted, ${result?.updated || 0} updated.`,
            "success"
          );
        }}
      />
      <UploadPhotosDialog
        open={uploadPhotosOpen}
        onClose={() => setUploadPhotosOpen(false)}
        selection={selected}
        onUploaded={(result) =>
          setNotice(result?.message || "Student photos uploaded successfully.")
        }
      />
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4500}
        onClose={() => setNotice("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={
            notice.toLowerCase().includes("error") || notice.toLowerCase().includes("missing")
              ? "error"
              : "success"
          }
          onClose={() => setNotice("")}
        >
          {notice}
        </Alert>
      </Snackbar>
    </Box>
  );
}
StudentFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selection: PropTypes.shape({
    batchId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    programId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    branchId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    batchName: PropTypes.string,
    programName: PropTypes.string,
    branchName: PropTypes.string,
  }).isRequired,
  student: PropTypes.object,
  lookups: PropTypes.shape({
    religions: PropTypes.array,
    castes: PropTypes.array,
    subcastes: PropTypes.array,
    bloodGroups: PropTypes.array,
    familyRelations: PropTypes.array,
  }),
  onSaved: PropTypes.func.isRequired,
};

StudentFormDialog.defaultProps = {
  student: null,
  lookups: {},
};

StudentActions.propTypes = {
  row: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onChangeProgram: PropTypes.func.isRequired,
  onChangeBranch: PropTypes.func.isRequired,
};
StudentsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selection: PropTypes.shape({
    batchId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    programId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    branchId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    batchName: PropTypes.string,
    programName: PropTypes.string,
    branchName: PropTypes.string,
  }).isRequired,
  lookups: PropTypes.shape({
    programs: PropTypes.array,
    branches: PropTypes.array,
  }),
  onRefresh: PropTypes.func.isRequired,
};

StudentsDialog.defaultProps = {
  lookups: {},
};

AdmitStudentsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selection: PropTypes.shape({
    batchId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    programId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    branchId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    batchName: PropTypes.string,
    programName: PropTypes.string,
    branchName: PropTypes.string,
  }).isRequired,
  onAdmitted: PropTypes.func.isRequired,
};
